import { splitOnCaseWords } from '../splitOnCaseWords';
import dedent from 'dedent';

export type PolicyPromptField<T> = {
  fieldTitle: string;
  fieldId: string;
  fieldDescription?: string;
  expectedDataType: string;
  fieldDescriptionTemplate?: (templateData: T) => string;
};

export function fieldOverride<T>(
  field: PolicyPromptField<T>,
  overrides: Partial<PolicyPromptField<T>>,
): PolicyPromptField<T> {
  return {
    ...field,
    ...overrides,
  };
}

export const policyMaximumBenefitAmount: PolicyPromptField<{}> = {
  fieldTitle: 'Maximum Benefit Amount',
  fieldId: 'policyMaximumBenefitAmount',
  fieldDescription: `This is the lifetime maximum benefit amount that the policy will pay out for long-term care costs. This amount is affected by any inflation protection that the policy has. Since we calculate the inflation protection ourselves, we want to capture the maximum benefit amount at the time the policy is purchased, not the inflated value that may be shown as an example for some future date. Importantly, policies can have unlimited max benefit, in which case you should enter "unlimited"`,
  expectedDataType: `Float number (do not round) or "unlimited" for unlimited policies, else null if not found`,
};

export const policyTotalLTCBalance: PolicyPromptField<{}> = {
  fieldTitle: 'Total LTC Balance/Benefit',
  fieldId: 'policyMaximumBenefitAmount',
  expectedDataType: `Float number (do not round), else null if not found`,
};

export const deathBenefitFaceAmount: PolicyPromptField<{}> = {
  fieldTitle: 'Face Amount (Death Benefit)',
  fieldId: 'policyMaximumBenefitAmount',
  fieldDescription: `This is the amount that the policy will pay out upon death, and which can be accessed early via qualifying long-term care costs. This amount is affected by any inflation protection that the policy has. Since we calculate the inflation protection ourselves, we want to capture the Face Amount or Death Benefit at the time the policy is purchased, not the inflated value that may be shown as an example for some future date. Sometimes you may find this amount under sections labeled as Day 1/initial benefits.`,
  expectedDataType: 'Float number (do not round), else null if not found',
};

export const policyMinimumGuaranteedBenefitAmount: PolicyPromptField<{}> = {
  fieldTitle: 'Minimum Guaranteed Death Benefit',
  fieldId: 'policyMinimumGuaranteedBenefitAmount',
  fieldDescription: `Some policies have a minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
  expectedDataType: 'Float number (do not round), else null if not found',
};

export const policyContinuationBenefitAmount: PolicyPromptField<{}> = {
  fieldTitle: 'Continuation of Benefits Amount',
  fieldId: 'policyContinuationBenefitAmount',
  fieldDescription: `The Continuation of Benefits Amount (CoB Amount), sometimes simply referred to as the LTC Benefits Total, is a separate pool from the Face Amount which can be accessed to fund long-term care. We model the policy such that the Face Amount will be used and fully exhausted first, and then the Continuation of Benefits Amount will be tapped next to pay for any remaining long-term care costs. Any unused Continuation of Benefits Amount is not paid out upon death in our models. Since we calculate the inflation protection ourselves, we want to capture the Continuation of Benefits Amount at the time the policy is purchased, not the inflated value that may be shown as an example for some future date. Sometimes you may find this amount under sections labeled as Day 1 benefits. Some Hybrid insurance policies have an Unlimited Continuation of Benefits Amount. In some Hybrid policies there is no Continuation of Benefits Amount, but there is instead a Total Long-Term Care Benefit Amount (policyTotalLTCBenefitAmount). In these cases calculate what the Continuation of Benefits Amount would have been by subtracting the Death Benefit (policyMaximumBenefitAmount) amount from policyTotalLTCBenefitAmount, and enter that value. `,
  expectedDataType: `Float number (do not round) or "unlimited" if the policy has an unlimited continuation of benefits amount. Else null`,
};

export const policyTotalLTCBenefitAmount: PolicyPromptField<{}> = {
  fieldTitle: 'Total Long-Term Care Benefits Amount',
  fieldId: 'policyTotalLTCBenefitAmount',
  fieldDescription: `This refers to the total amount of benefit that can be applied to pay for long-term care costs. If there is a Continuation of Benefits Amount then this is typically the sum of the Continuation of Benefits and the Death Benefit amounts. If there is not a Continuation of Benefits Amount then this is often just the amount that is available for long-term care payments and every dollar drawn from this amount is typically also drawn from the Death Benefit Amount.`,
  expectedDataType:
    'Float number (do not round), else null if not found or if Continuation of Benefits Amount is unlimited',
};

export const policyBenefitPeriodMonths = {
  fieldTitle: 'Benefit Period Months',
  fieldId: 'policyBenefitPeriodMonths',
  fieldDescription: `Here is where you'll enter the maximum amount of time that your policy will pay out the benefits. This is a key feature to Short Term Care policies, which, unlike most other types of long-term care policies, are limited in the total duration that they pay out. If the benefit period is given in days or years, convert this to months. For example, if the benefit period is 3 years, enter 3 x 12 = 36 months.`,
  expectedDataType: 'Integer number of months or null if not found',
};

export const policyInflationProtectionPercentage = {
  fieldTitle: 'Inflation Protection Percentage',
  fieldId: 'policyInflationProtection',
  fieldDescription: dedent`Inflation protection is the percentage that a policy's benefits (e.g., maximum benefit, continuation of benefits) increase over time to keep pace with inflation. Some common values are 0, 2, 3, or 5 percent. If the policy has no inflation protection, enter 0. If the policy does not specify, enter null.`,
  expectedDataType: 'Float number or 0 if not found',
};

export const policyInflationProtectionType = {
  fieldTitle: 'Inflation Protection Type (Compound or Simple)',
  fieldId: 'policyInflationProtectionType',
  fieldDescription: dedent`Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
  expectedDataType: 'String value (compound or simple) or null if not found',
};

export const isPrimaryPolicyHolder = {
  fieldTitle: 'Primary Policy Holder Match',
  fieldId: 'isPrimaryPolicyHolder',
  expectedDataType: 'Yes or No, or null if not found',
  fieldDescriptionTemplate: (
    body: PolicyUploadTextAnalysisRequest,
  ) => dedent`Primary Policy Holder Match:
  The primary policyholder is the person who owns the policy and is the primary insured. Their name, age, and gender can typically be found on the first few pages of the policy document. This field should be marked as "Yes" if the name: ${body.clientFullName}, birth year: ${body.clientBirthYear}, and gender: ${body.clientGenderAtBirth} match the information in the policy document for the primary insured, and "No" if the information does not match. If only some of the data about the primary insured is provided, match on what is provided. You may have to infer birth year based on the provided age and the policy purchase year. This data ("Yes"/"No") will be used to determine "Expected Policy Activation Age".
  `,
};

export const expectedPolicyActivationAge = {
  fieldTitle: 'Expected Policy Activation Age',
  fieldId: 'expectedPolicyActivationAge',
  expectedDataType: 'Integer or null if not found',
  fieldDescriptionTemplate: (
    body: PolicyUploadTextAnalysisRequest,
  ) => dedent`Primary Expected Policy Activation Age:
This is the age of the primary policy holder when the policy is expected to activate (i.e. first partner goes on claim). In order to determine this, we need to know "Primary Policy Holder Match". If the primary insured's information matches the following: name: ${body.clientFullName}, birth year: ${body.clientBirthYear}, and gender: ${body.clientGenderAtBirth} (i.e. Primary Policy Holder Match equals "Yes"), then use the age ${body.policyActivationAgeClientIsPolicyHolder}. If Primary Policy Holder Match equals "No", then use the age ${body.policyActivationAgePartnerIsPolicyHolder}. This age will be used to look up the policy's "Total LTC Balance/Benefit" and "Monthly Max Benefits" on the provided tables in the policy document.
  `,
};

export const monthlyBenefitWithdrawlRate = {
  fieldTitle: 'Monthly Benefit Withdrawal Rate',
  fieldId: 'policyMonthlyWithdrawalRate',
  fieldDescription: dedent`The monthly benefit withdrawal rate is the percentage of the death benefit that can be withdrawn each month to pay for long-term care costs. This is typically between 1% and 2% of the death benefit/maximum LTC benefit. Look for text describing how to calculate the maxmimum monthly benefit by multipling the expected LTC benefit by a certain percentage. If the policy does not specify a monthly benefit withdrawal rate, enter null. `,
  expectedDataType: 'Float number or null if not found',
};

export const deathBenefitAtAge = {
  fieldTitle: 'Death Benefit (Face Amount)',
  fieldId: 'policyMaximumBenefitAmount',
  fieldDescriptionTemplate: (
    body: PolicyUploadTextAnalysisRequest,
  ) => dedent`First check whether the "Initial Death Benefit Option" is "level". You can find this information on the first few pages. If so, DO NOT look up the death benefit at age from the table. Just use the initial face amount/death benefit also found on the first few pages.
  If "Initial Death Benefit Option" is "increasing", then look up the "Death Benefit" in the provided tables. The tables are indexed on "age" and "year". We need an accurate look up of a specific value from this table based on the "age" column (not "year"). The value I need is the "death benefit" under the column grouping "Non guaranteed current projections". Look up the value for age ${body.ltcAtAge} and take your time on finding the precise value.`,
  expectedDataType: 'Float number (do not round), else null if not found',
};

export const hasCobRider = {
  fieldTitle: 'Has COB Rider Option',
  fieldId: 'policyHasCobRider',
  fieldDescription:
    "Look for a table around page 16. The table should include the following row labels 'Initial Total Death Benefit', 'Initial Total LTC Benefit Balance', and 'Initial Monthly Benefit'. Once you found the table, look for a column labeled 'Base Policy + COB Rider'. If you find this column, then the policy has a COB rider. If you do not find this column and there is only a column labeeld 'Base Policy', then the policy does not have a COB rider.",
  expectedDataType: 'Yes or No',
};

export const homeCareWaitingPeriodDays = {
  fieldTitle: 'Waiting/Elimination Period for Home Care',
  fieldId: 'homeCareWaitingPeriodDays',
  fieldDescription: `An elimination period in a long-term care (LTC) insurance policy is a waiting period during which the policyholder must cover care costs out-of-pocket before insurance benefits begin. Typically ranging from 30 to 180 days (but can also be 0), it starts when the insured is deemed eligible for benefits due to needing assistance with daily activities or having a cognitive impairment. Some policies have a different waiting period for home care vs. care provided at a facility, but assume the same waiting period for both if not specified.`,
  expectedDataType:
    'Integer number of days or 0 if the policy does not reference a waiting or elimination period',
};

export const facilityCareWaitingPeriodDays = {
  fieldTitle: 'Waiting/Elimination Period for Facility Care',
  fieldId: 'facilityCareWaitingPeriodDays',
  fieldDescription: `An elimination period in a long-term care (LTC) insurance policy is a waiting period during which the policyholder must cover care costs out-of-pocket before insurance benefits begin. Typically ranging from 30 to 180 days (but can also be 0), it starts when the insured is deemed eligible for benefits due to needing assistance with daily activities or having a cognitive impairment. Some policies have a different waiting period for home care vs. care provided at a facility, but assume the same waiting period for both if not specified.`,
  expectedDataType:
    'Integer number of days or 0 if the policy does not reference a waiting or elimination period',
};

export const policyPremiumStartYear = {
  fieldTitle: 'Policy Purchase Year',
  fieldId: 'policyPremiumStartYear',
  fieldDescription: `The is the year that the policy was purchased and premiums began to be paid. If you are modeling a policy that the client already owns, you can enter the year in which it was purchased. If you are evaluating a new policy, you can enter the current year in order to model the investment and payout as if your client were to purchase the policy today.`,
  expectedDataType: 'Integer year (4-digit format)',
};

export const policyPremiumMonthlyCost = {
  fieldTitle: 'Monthly Premium',
  fieldId: 'policyPremiumMonthlyCost',
  fieldDescription: `This is the amount being paid into the policy each month. If the policy has annual premiums then please divide them by 12 to get the monthly premium amount. `,
  expectedDataType:
    'Float number (do not round). If the policy involves a single premium or lump-sum payment, enter 0 for the Monthly Premium and provide the lump-sum amount in the Lump-sum Payment field below. If the monthly premium is not found, enter 0.',
};

export const policyLimitedPayYears = {
  fieldTitle: 'Limited Pay Period',
  fieldId: 'policyLimitedPayYears',
  fieldDescription: `If the policy has a limited pay period, such as a 3 pay, 5, pay, or 10 pay, you can enter that in the next field. Otherwise, we will model premium payments from the policy purchase year up until the date that the client is predicted to go on claim with the policy. If your policy has a limited pay period, you can enter the total number of years for the pay period here.`,
  expectedDataType: 'Integer number of years or null if not found',
};

export const policyLumpSumPayment = {
  fieldTitle: 'Lump-Sum Payment',
  fieldId: 'policyLumpSumPayment',
  fieldDescription: `Some insurance policies are paid for with a single premium or lump-sum payment. If that is the case for your policy, you can enter the amount here. If the lump-sum is the only premium for the policy, then you'll also need to enter 0 for Monthly Premium.`,
  expectedDataType: 'Float number (do not round) else null if not found',
};

export const policyPaymentType = {
  fieldTitle: 'Payment Type (Reimbursement or Indemnity)',
  fieldId: 'policyPaymentType',
  fieldDescription: `Insurance policies can pay out via reimbursements or via a fixed indemnity benefit amount. For reimbursement plans, actual expenses are reimbursed up some maximum amount. For indemnity plans, a fixed amount is paid out regardless of the actual expenses incurred.`,
  expectedDataType:
    'String value (reimbursement or indemnity) or null if not found',
};

export const policyPremiumIncreaseRate = {
  fieldTitle: 'Premium Increase Rate',
  fieldId: 'policyPremiumIncreaseRate',
  fieldDescription: `This is the percentage that the policy's premiums increase each year. Some policies have fixed premiums that do not increase. If the policy does not specify a premium increase rate, enter null.`,
  expectedDataType: 'Float number or 0 if not found',
};

export const policyPremiumIncreaseYears = {
  fieldTitle: 'Premium Increase Duration (Years)',
  fieldId: 'policyPremiumIncreaseYears',
  fieldDescription: `Number of years the premium increase rate applies. Only relevant if the policy has a premium increase rate. If the policy does not specify a duration for premium increases, enter null.`,
  expectedDataType: 'Float number or null if not found',
};

export const policyInflationProtectionOnFaceAmount = {
  fieldTitle: 'Inflation Protection on Face Amount',
  fieldId: 'policyInflationProtectionOnFaceAmount',
  fieldDescription: `The percentage the face amount increases to keep up with inflation.`,
  expectedDataType: 'Float number or null if not found',
};

export const policyInflationProtectionOnCOBAmount = {
  fieldTitle: 'Inflation Protection on COB Amount',
  fieldId: 'policyInflationProtectionOnCOBAmount',
  fieldDescription: `The percentage the COB amount increases to keep up with inflation.`,
  expectedDataType: 'Float number or null if not found',
};

export const policyInflationProtectionYears = {
  fieldTitle: 'Inflation Protection Period',
  fieldId: 'policyInflationProtectionYears',
  fieldDescription: `Number of years the value of benefits will increase. If left blank, the benefits will increase until projected end of care year.`,
  expectedDataType: 'Float number or null if not found',
};

export const commonFields: PolicyPromptField<any>[] = [
  homeCareWaitingPeriodDays,
  facilityCareWaitingPeriodDays,
  policyPremiumStartYear,
  policyPremiumMonthlyCost,
  policyLimitedPayYears,
  policyLumpSumPayment,
  policyPaymentType,
];

export enum MaxBenefitPeriod {
  monthly = 'monthly',
  annual = 'annual',
  daily = 'daily',
}

const benefitPeriodRanges = {
  [MaxBenefitPeriod.monthly]: [18000, 180000],
  [MaxBenefitPeriod.annual]: [1500, 15000],
  [MaxBenefitPeriod.daily]: [50, 500],
};

const benefitPeriodFieldKeys = {
  [MaxBenefitPeriod.daily]: [
    'dailyHomeBenefitAmount',
    'dailyIndependentLivingBenefitAmount',
    'dailyAssistedLivingBenefitAmount',
    'dailyNursingHomeBenefitAmount',
  ],
  [MaxBenefitPeriod.monthly]: [
    'monthlyHomeBenefitAmount',
    'monthlyIndependentLivingBenefitAmount',
    'monthlyAssistedLivingBenefitAmount',
    'monthlyNursingHomeBenefitAmount',
  ],
  [MaxBenefitPeriod.annual]: [
    'annualHomeBenefitAmount',
    'annualIndependentLivingBenefitAmount',
    'annualAssistedLivingBenefitAmount',
    'annualNursingHomeBenefitAmount',
  ],
};

const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

const removeTerms = (text: string, terms: string[]) => {
  let result = text;
  terms.forEach(term => {
    result = result.replace(new RegExp(term, 'i'), ''); // 'i' flag for case-insensitive replace
  });
  return result;
};

export const getMaxBenefitBenefitPerPeriodFields = (
  benefitPeriods: MaxBenefitPeriod[],
) => {
  return benefitPeriods.flatMap(period => {
    return benefitPeriodFieldKeys[period].map(fieldId => {
      const careEnv = removeTerms(fieldId, [
        'daily',
        'monthly',
        'annual',
        'BenefitAmount',
      ]);
      const careEnvironmentLabel = splitOnCaseWords(careEnv).join(' ');
      const periodCapitalized = capitalizeFirstLetter(period);

      return {
        fieldTitle: `${periodCapitalized} Max Benefit for ${careEnvironmentLabel} Care`,
        fieldId,
        expectedDataType: `Float number (do not round) else null if not found. Usually ranges between $${benefitPeriodRanges[period][0]} and $${benefitPeriodRanges[period][1]} per day.`,
      };
    });
  });
};
